import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Container,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import ProjectCreationPrompt from '../components/ProjectCreationPrompt';
import ProjectManagementModal from '../components/ProjectManagementModal';
import {
  ProjectsCreateAndLink,
  SearchAndFilters,
  ProjectStats,
  ProjectCard,
  SubscriptionSummary
} from '../components/projects';
import API_BASE from '../apiBase';
import { getStoredUser } from '../utils/userStorage';


function Projects() {
  const [projects, setProjects] = useState([]);
  const [showProjectPrompt, setShowProjectPrompt] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showProjectManagementModal, setShowProjectManagementModal] = useState(false);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [isTrialFlow, setIsTrialFlow] = useState(false);
  const [projectSubscriptionStatuses, setProjectSubscriptionStatuses] = useState({});
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // New state for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [repoFilter, setRepoFilter] = useState('all');

  // State for project selection
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // State for lazy loading
  const [displayedCount, setDisplayedCount] = useState(4); // Initial batch size
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  // Compute filtered projects using useMemo
  const filteredProjects = useMemo(() => {
    return projects.filter((projectData) => {
      const project = projectData.project || projectData;
      if (searchTerm && project.name && !project.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (statusFilter !== 'all') {
        const userInfo = getStoredUser() || {};
        const currentUserEmail = userInfo.email || '';
        const isCreatedByUser = project.created_by_info?.email === currentUserEmail;
        const isReadOnly = project.created_by_info?.email !== currentUserEmail;
        
        if (statusFilter === 'your-projects' && !isCreatedByUser) return false;
        if (statusFilter === 'read-only' && !isReadOnly) return false;
        if (statusFilter === 'active' && isReadOnly) return false;
        if (statusFilter === 'inactive') {
          const subscription = projectData.subscription;
          if (subscription) {
            const now = new Date();
            const endDate = new Date(subscription.end_date);
            const isExpired = endDate < now;
            const isActive = subscription.status === 'active' || subscription.status === 'trial';
            if (isActive && !isExpired) return false;
          }
        }
      }
      if (repoFilter !== 'all' && project.repository_type !== repoFilter) {
        return false;
      }
      return true;
    });
  }, [projects, searchTerm, statusFilter, repoFilter]);

  useEffect(() => {
    fetchProjects();
    fetchSubscriptionSummary();

    // Check for payment success and show project management modal
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentId = urlParams.get('payment_id');

    if (paymentStatus === 'success' && paymentId) {
      setShowProjectManagementModal(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check if user has trial payment and no projects - show welcome modal
    checkTrialWelcome();
  }, []);

  // Compute subscription statuses from projects-with-subscriptions data
  // No need to make additional API calls since subscription data is already included
  useEffect(() => {
    if (projects.length === 0) {
      setProjectSubscriptionStatuses({});
      return;
    }

    const computeStatuses = async () => {
      const statuses = {};
      const fetchPromises = [];
      
      projects.forEach((projectItem) => {
        if (!projectItem) return;
        const project = projectItem.project || projectItem;
        const subscription = projectItem.subscription;
        
        if (subscription) {
          // Use subscription data from projects-with-subscriptions (most common case)
          const now = new Date();
          const endDate = new Date(subscription.end_date);
          const isExpired = endDate < now;
          const isActive = subscription.status === 'active' || subscription.status === 'trial';
          const canDelete = !isActive && isExpired;
          statuses[project.id] = { isActive, isExpired, canDelete };
        } else if (project.subscription_id) {
          // Only fetch if subscription data is missing (fallback - should rarely happen)
          fetchPromises.push(
            checkProjectSubscriptionStatus(project.id, project.subscription_id)
              .then(status => ({ projectId: project.id, status }))
              .catch(() => ({ 
                projectId: project.id, 
                status: { isActive: false, isExpired: true, canDelete: true } 
              }))
          );
          // Set default while fetching
          statuses[project.id] = { isActive: false, isExpired: true, canDelete: true };
        } else {
          statuses[project.id] = { isActive: false, isExpired: true, canDelete: true };
        }
      });
      
      // Batch fetch any missing subscription statuses in parallel
      if (fetchPromises.length > 0) {
        const results = await Promise.all(fetchPromises);
        results.forEach(({ projectId, status }) => {
          statuses[projectId] = status;
        });
      }
      
      setProjectSubscriptionStatuses(statuses);
    };

    computeStatuses();
  }, [projects]);

  // Reset displayed count when filtered projects change (e.g., filters applied)
  useEffect(() => {
    setDisplayedCount(4);
  }, [searchTerm, statusFilter, repoFilter]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (displayedCount >= filteredProjects.length) {
      return; // All projects are already displayed
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && displayedCount < filteredProjects.length) {
          setIsLoadingMore(true);
          // Simulate a small delay for smooth loading
          setTimeout(() => {
            setDisplayedCount((prev) => Math.min(prev + 4, filteredProjects.length));
            setIsLoadingMore(false);
          }, 300);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [isLoadingMore, displayedCount, filteredProjects.length]);


  const checkTrialWelcome = async () => {
    try {
      // Check if user has a trial payment
      const paymentResponse = await axios.get(`${API_BASE}/api/payments/current-subscription`);

      if (paymentResponse.data.has_active_subscription && paymentResponse.data.subscription) {
        const subscription = paymentResponse.data.subscription;
        if (subscription.status === 'trial') {
          const projectsResponse = await axios.get(`${API_BASE}/api/projects`);
          if (projectsResponse.data.length === 0) {
            setIsTrialFlow(true);
            setShowProjectPrompt(true);
          }
        }
      }
    } catch (error) {
      console.log('No trial payment found or error checking trial status:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const { data } = await axios.get(`${API_BASE}/api/projects-with-subscriptions`);
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchSubscriptionSummary = async () => {
    try {
      const [summaryResponse, currentResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/payments/subscription-summary`),
        axios
          .get(`${API_BASE}/api/payments/current-subscription`)
          .catch((error) => {
            if (error.response?.status === 404) {
              return { data: { has_active_subscription: false } };
            }
            throw error;
          }),
      ]);

      setSubscriptionSummary({
        ...summaryResponse.data,
        has_active_subscription:
          currentResponse?.data?.has_active_subscription ?? true,
      });
    } catch (error) {
      console.log('Failed to fetch subscription summary:', error);
    }
  };

  const checkProjectSubscriptionStatus = async (projectId, subscriptionId) => {
    if (!subscriptionId) {
      return { isActive: false, isExpired: true, canDelete: true };
    }
    try {
      const response = await axios.get(`${API_BASE}/api/project-subscription/${subscriptionId}`);
      const subscription = response.data;
      const now = new Date();
      const endDate = new Date(subscription.end_date);
      const isExpired = endDate < now;
      const isActive = subscription.status === 'active' || subscription.status === 'trial';
      const canDelete = !isActive && isExpired;
      return { isActive, isExpired, canDelete };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return { isActive: false, isExpired: true, canDelete: true };
    }
  };

  const fetchProjectSubscriptionStatuses = async () => {
    // Use subscription data from projects-with-subscriptions if available
    // Only fetch if subscription data is missing
    const statuses = {};
    const promises = [];
    
    for (let i = 0; i < projects.length; i++) {
      const projectItem = projects[i];
      if (!projectItem) continue;
      const project = projectItem.project || projectItem;
      const subscription = projectItem.subscription;
      
      // If subscription data is already available from projects-with-subscriptions, use it
      if (subscription) {
        const now = new Date();
        const endDate = new Date(subscription.end_date);
        const isExpired = endDate < now;
        const isActive = subscription.status === 'active' || subscription.status === 'trial';
        const canDelete = !isActive && isExpired;
        statuses[project.id] = { isActive, isExpired, canDelete };
      } else if (project.subscription_id) {
        // Only fetch if subscription data is missing
        promises.push(
          checkProjectSubscriptionStatus(project.id, project.subscription_id)
            .then(status => ({ projectId: project.id, status }))
        );
      } else {
        statuses[project.id] = { isActive: false, isExpired: true, canDelete: true };
      }
    }
    
    // Batch fetch remaining subscription statuses in parallel
    if (promises.length > 0) {
      const results = await Promise.all(promises);
      results.forEach(({ projectId, status }) => {
        statuses[projectId] = status;
      });
    }
    
    setProjectSubscriptionStatuses(statuses);
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      const userInfo = getStoredUser() || {};
      const currentUserEmail = userInfo.email || '';
      if (project.created_by_info?.email !== currentUserEmail) {
        setError('Only the project creator can edit project details');
        return;
      }
    }
    setCurrentProject(project);
    setShowProjectPrompt(true);
  };

  const handleCloseDialog = () => {
    setShowProjectPrompt(false);
    setCurrentProject(null);
    setError('');
  };

  const handleSaveProject = async (projectData) => {
    const isUpdating = !!projectData.id;
    const url = isUpdating ? `${API_BASE}/api/projects/${projectData.id}` : `${API_BASE}/api/projects`;
    const method = isUpdating ? 'put' : 'post';

    const pick = (v) => (typeof v === 'string' && v.trim() !== '' ? v : undefined);
    const nestedRepo = projectData.repository_config || {};
    const nestedAnalyzer = projectData.analyzer_config || {};
    const nestedJira = projectData.jira_config || {};
    const nestedAzureBoards = projectData.azure_boards_config || {};
    const nestedTrello = projectData.trello_config || {};
    const nestedZohoSprints = projectData.zoho_sprints_config || {};

    let repoPayload = {};
    if (projectData.repository_type === 'github') {
      const token = pick(nestedRepo.token) || pick(projectData.github_token);
      if (token) repoPayload.token = token;
    } else if (projectData.repository_type === 'gitlab') {
      const token = pick(nestedRepo.token) || pick(projectData.gitlab_token);
      if (token) repoPayload.token = token;
    } else if (projectData.repository_type === 'bitbucket') {
      const token = pick(nestedRepo.token) || pick(projectData.bitbucket_token);
      if (token) repoPayload.token = token;
    } else if (projectData.repository_type === 'azure') {
      const organization = pick(nestedRepo.organization) || pick(projectData.azure_organization);
      const project = pick(nestedRepo.project) || pick(projectData.azure_project);
      const token = pick(nestedRepo.token) || pick(projectData.azure_token);
      if (organization) repoPayload.organization = organization;
      if (project) repoPayload.project = project;
      if (token) repoPayload.token = token;
    }

    const apiKey = pick(nestedAnalyzer.api_key) || pick(projectData.analyzer_api_key);
    const analyzerPayload = {};
    if (apiKey) analyzerPayload.api_key = apiKey;

    let jiraPayload;
    const jiraApiToken = pick(nestedJira.api_token) || pick(projectData.jira_token);
    const jiraBaseURL = pick(nestedJira.base_url) || pick(projectData.jira_url);
    const jiraUsername = pick(nestedJira.username) || pick(projectData.jira_user);
    if (projectData.jira_enabled || jiraApiToken || jiraBaseURL || jiraUsername) {
      jiraPayload = {};
      if (jiraApiToken) jiraPayload.api_token = jiraApiToken;
      if (jiraBaseURL) jiraPayload.base_url = jiraBaseURL;
      if (jiraUsername) jiraPayload.username = jiraUsername;
    }

    let azureBoardsPayload;
    const azureBoardsToken = pick(nestedAzureBoards.token) || pick(projectData.azure_boards_token);
    const azureBoardsOrg = pick(nestedAzureBoards.organization) || pick(projectData.azure_boards_org);
    const azureBoardsProject = pick(nestedAzureBoards.project) || pick(projectData.azure_boards_project);
    if (projectData.azure_boards_enabled || azureBoardsToken || azureBoardsOrg || azureBoardsProject) {
      azureBoardsPayload = {};
      if (azureBoardsToken) azureBoardsPayload.token = azureBoardsToken;
      if (azureBoardsOrg) azureBoardsPayload.organization = azureBoardsOrg;
      if (azureBoardsProject) azureBoardsPayload.project = azureBoardsProject;
    } else if (projectData.azure_boards_config && Object.keys(projectData.azure_boards_config).length > 0) {
      // If azure_boards_config is explicitly provided but empty, still include it
      azureBoardsPayload = projectData.azure_boards_config;
    }

    let trelloPayload;
    const trelloApiKey = pick(nestedTrello.api_key) || pick(projectData.trello_api_key);
    const trelloToken = pick(nestedTrello.token) || pick(projectData.trello_token);
    if (projectData.trello_enabled || trelloApiKey || trelloToken) {
      trelloPayload = {};
      if (trelloApiKey) trelloPayload.api_key = trelloApiKey;
      if (trelloToken) trelloPayload.token = trelloToken;
    } else if (projectData.trello_config && Object.keys(projectData.trello_config).length > 0) {
      trelloPayload = projectData.trello_config;
    }

    let zohoSprintsPayload;
    if (projectData.zoho_sprints_enabled || Object.keys(nestedZohoSprints).length > 0) {
      zohoSprintsPayload = {};
      if (nestedZohoSprints.team_id) zohoSprintsPayload.team_id = nestedZohoSprints.team_id;
      if (nestedZohoSprints.project_id) zohoSprintsPayload.project_id = nestedZohoSprints.project_id;
      if (nestedZohoSprints.domain) zohoSprintsPayload.domain = nestedZohoSprints.domain;
      if (nestedZohoSprints.client_id) zohoSprintsPayload.client_id = nestedZohoSprints.client_id;
      if (pick(nestedZohoSprints.client_secret)) zohoSprintsPayload.client_secret = nestedZohoSprints.client_secret;
      if (pick(nestedZohoSprints.access_token)) zohoSprintsPayload.access_token = nestedZohoSprints.access_token;
      if (pick(nestedZohoSprints.refresh_token)) zohoSprintsPayload.refresh_token = nestedZohoSprints.refresh_token;
      if (pick(nestedZohoSprints.grant_token)) zohoSprintsPayload.grant_token = nestedZohoSprints.grant_token;
    }

    const payload = {
      name: projectData.name,
      description: projectData.description,
      business_context: projectData.business_context,
      group_id: projectData.group_id,
      repository_type: projectData.repository_type,
      analyzer_type: projectData.analyzer_type,
      jira_enabled: projectData.jira_enabled || false,
      azure_boards_enabled: projectData.azure_boards_enabled || false,
      trello_enabled: projectData.trello_enabled || false,
      zoho_sprints_enabled: projectData.zoho_sprints_enabled || false,
      created_by_info: projectData.created_by_info,
      user_emails: projectData.user_emails,
      review_comment_mode: projectData.review_comment_mode || 'inline',
    };

    payload.repository_config = repoPayload;
    if (Object.keys(analyzerPayload).length > 0) payload.analyzer_config = analyzerPayload;
    if (jiraPayload) payload.jira_config = jiraPayload;
    if (azureBoardsPayload) payload.azure_boards_config = azureBoardsPayload;
    if (trelloPayload) payload.trello_config = trelloPayload;
    if (zohoSprintsPayload) payload.zoho_sprints_config = zohoSprintsPayload;
    if (projectData.compliance) payload.compliance = projectData.compliance;
    if (projectData.control_preference) payload.control_preference = projectData.control_preference;

    try {
      await axios[method](url, payload);
      setSuccess(`Project ${isUpdating ? 'updated' : 'created'} successfully!`);
      fetchProjects();
      fetchSubscriptionSummary();
      handleCloseDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save project.';
      throw new Error(errorMessage);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await axios.delete(`${API_BASE}/api/projects/${projectId}`);
        setSuccess('Project deleted successfully');
        fetchProjects();
      } catch (error) {
        setError('Failed to delete project');
      }
    }
  };

  const getTruncatedKey = (key) => {
    if (!key) return '';
    if (key.length <= 12) return key;
    return key.slice(0, 6) + '...' + key.slice(-4);
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopySuccess(true);
  };

  const handleProjectManagementComplete = () => {
    setShowProjectManagementModal(false);
    fetchProjects();
    fetchSubscriptionSummary();
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setIsSelectAllChecked(checked);
    if (checked) {
      const allProjectIds = new Set(filteredProjects.map(projectData => {
        const project = projectData.project || projectData;
        return project.id;
      }));
      setSelectedProjects(allProjectIds);
    } else {
      setSelectedProjects(new Set());
    }
  };

  const handleSelectProject = (projectId, checked) => {
    const newSelected = new Set(selectedProjects);
    if (checked) {
      newSelected.add(projectId);
    } else {
      newSelected.delete(projectId);
    }
    setSelectedProjects(newSelected);

    // Update select all checkbox state
    const allProjectIds = filteredProjects.map(projectData => {
      const project = projectData.project || projectData;
      return project.id;
    });
    setIsSelectAllChecked(newSelected.size === allProjectIds.length && allProjectIds.length > 0);
  };

  // Delete selected projects functionality is currently disabled.
  const handleDeleteSelected = () => {
    // Functionality disabled for now.
    // Optionally, you could show a message or do nothing.
    // setError('Bulk delete is currently disabled.');
  };

  const handleCancelSelection = () => {
    setSelectedProjects(new Set());
    setIsSelectAllChecked(false);
  };


  return (
    <Box sx={{ bgcolor: "#fbfbfb", minHeight: "100vh", color: "#1f1f1f", p: { xs: 1, sm: 2, md: 6 }, }}>
      <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        {/* Alerts */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{ mb: 4, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess('')}
            sx={{ mb: 4, borderRadius: 2 }}
          >
            {success}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'stretch', md: 'space-between' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 2, md: 2 },
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '70%' },
              flexShrink: 1,
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <SearchAndFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              repoFilter={repoFilter}
              onRepoFilterChange={setRepoFilter}
            />
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '30%' },
              display: 'flex',
              justifyContent: { xs: 'flex-end', md: 'flex-end' },
              mt: { xs: 2, md: 0 },
            }}
          >
            <ProjectsCreateAndLink
              onAddProject={() => setShowProjectManagementModal(true)}
              onLinkExisting={() => setShowProjectManagementModal(true)}
            />
          </Box>
        </Box>

        {/* Subscription Summary Display */}
        <SubscriptionSummary subscriptionSummary={subscriptionSummary} />

        {/* Selection Banner */}
        {/* {selectedProjects.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderRadius: 2, p: 3, mb: 3, border: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ color: '#1f1f1f', fontWeight: 600 }}>
              {selectedProjects.size} project{selectedProjects.size !== 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
                sx={{
                  bgcolor: '#ef4444',
                  '&:hover': { bgcolor: '#dc2626' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Delete Selected
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelSelection}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )} */}

        {/* Project Statistics */}
        <ProjectStats projects={projects} />

        {/* Select All Checkbox */}
        {/* {filteredProjects.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSelectAllChecked}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{
                    color: '#000',
                    '&.Mui-checked': {
                      color: '#00bfff',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f1f1f' }}>
                  Select all projects
                </Typography>
              }
            />
          </Box>
        )} */}

        {/* Projects Grid */}
        {isLoadingProjects ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {filteredProjects.length === 0 && !error && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      No projects found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Get started by adding a new project to begin code analysis.
                    </Typography>
                  </Box>
                </Grid>
              )}

              {filteredProjects.slice(0, displayedCount).map((projectData) => {
                const project = projectData.project || projectData;
                const userInfo = getStoredUser() || {};
                const currentUserEmail = userInfo.email || '';
                const canEdit = project.created_by_info?.email === currentUserEmail;
                const subscriptionStatus = projectSubscriptionStatuses[project.id];
                const canDelete = canEdit && (!subscriptionStatus || subscriptionStatus.canDelete);
                const planName = (projectData.subscription && (projectData.subscription.plan_name || projectData.subscription.plan?.name)) || project.subscription_plan_name || null;

                return (
                  <Grid item xs={12} md={6} key={project.id}>
                    <ProjectCard
                      project={{ ...project, subscription_plan_name: planName }}
                      onEdit={handleOpenDialog}
                      onDelete={handleDeleteProject}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      isSelected={selectedProjects.has(project.id)}
                      onSelect={(checked) => handleSelectProject(project.id, checked)}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {/* Lazy loading trigger and loading indicator */}
            {filteredProjects.length > displayedCount && (
              <Box
                ref={observerTarget}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  minHeight: 100,
                }}
              >
                {isLoadingMore && (
                  <CircularProgress size={40} />
                )}
              </Box>
            )}
          </>
        )}

        {showProjectPrompt && (
          <ProjectCreationPrompt
            open={showProjectPrompt}
            onClose={handleCloseDialog}
            onSave={handleSaveProject}
            project={currentProject}
            isTrial={isTrialFlow}
          />
        )}

        {showProjectManagementModal && (
          <ProjectManagementModal
            open={showProjectManagementModal}
            onClose={handleProjectManagementComplete}
          />
        )}

        {/* Snackbar for copy feedback */}
        <Snackbar
          open={copySuccess}
          autoHideDuration={1500}
          onClose={() => setCopySuccess(false)}
          message="Copied to clipboard!"
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </Container>
    </Box>
  );
}

export default Projects; 
